import type { ApiNode, DailyPrep, WeeklyPrep } from '@goalbuddy/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { HealthPanel } from '@/components/health-panel';
import { NodeRow } from '@/components/node-row';
import { Badge, Button, Card, Empty, Label, PageHeader, Spinner, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { useUpdateNode } from '@/lib/hooks';
import { fmtDate, fmtDateTime } from '@/lib/utils';

const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());

function Section({ title, children, count }: { title: string; children: React.ReactNode; count?: number }) {
  return (
    <Card className="p-4">
      <h2 className="mb-2 flex items-center gap-2 font-semibold">
        {title} {count !== undefined && <Badge>{count}</Badge>}
      </h2>
      {children}
    </Card>
  );
}

function Rows({
  nodes,
  empty,
  extra,
}: {
  nodes: ApiNode[];
  empty: string;
  extra?: (n: ApiNode) => React.ReactNode;
}) {
  if (!nodes.length) return <div className="text-sm text-muted-fg">{empty}</div>;
  return (
    <div className="space-y-0.5">
      {nodes.map((n) => (
        <NodeRow key={n.id} node={n} extra={extra?.(n)} />
      ))}
    </div>
  );
}

function Questions({
  questions,
  answers,
  setAnswers,
}: {
  questions: { key: string; question: string; hint?: string }[];
  answers: Record<string, string>;
  setAnswers: (a: Record<string, string>) => void;
}) {
  return (
    <div className="space-y-3">
      {questions.map((q) => (
        <div key={q.key}>
          <Label className="text-sm text-fg">{q.question}</Label>
          {q.hint && <div className="text-xs text-muted-fg">{q.hint}</div>}
          <Textarea
            rows={2}
            value={answers[q.key] ?? ''}
            onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}

function PastReviews({ type }: { type: 'daily_prep' | 'weekly_review' }) {
  const { data } = useQuery({ queryKey: ['reviews', type], queryFn: () => api.reviews(type, 5) });
  if (!data?.length) return null;
  return (
    <Section title="Previous reviews">
      <ul className="space-y-2 text-sm">
        {data.map((r) => (
          <li key={r.id}>
            <span className="text-xs text-muted-fg">
              {fmtDate(r.date)} · template v{r.template_version ?? '?'}
            </span>
            {r.summary_md && <div>{r.summary_md}</div>}
            {r.insights_next_time && <div className="text-muted-fg">Insight: {r.insights_next_time}</div>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function DailyReviewPage() {
  const date = todayLocal();
  const { data: prep, isLoading } = useQuery({
    queryKey: ['prep', 'daily', date],
    queryFn: () => api.dailyPrep(date),
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [focus, setFocus] = useState<number[]>([]);
  const update = useUpdateNode();
  const navigate = useNavigate();
  useEffect(() => {
    if (!prep) return;
    const existing = prep.focus_tomorrow.items.map((i) => i.node.id);
    setFocus(existing.length ? existing : prep.focus_suggestion.map((s) => s.node_id));
  }, [prep]);
  const save = useMutation({
    mutationFn: () =>
      api.createReview({
        type: 'daily_prep',
        date,
        template_id: prep!.template.id,
        answers,
        summary_md: summary || undefined,
        focus_node_ids: focus,
        focus_confirmed: true,
      }),
    onSuccess: () => {
      toast.success('Daily prep saved, focus for tomorrow confirmed');
      navigate('/');
    },
  });
  if (isLoading || !prep) return <Spinner />;
  const p: DailyPrep = prep;
  const candidates = new Map<number, string>();
  for (const s of p.focus_suggestion) candidates.set(s.node_id, `${s.title} (${s.reasons.join(', ')})`);
  for (const i of p.focus_tomorrow.items) candidates.set(i.node.id, i.node.title);

  return (
    <div className="space-y-4">
      <PageHeader title="Daily prep" subtitle={`Close ${fmtDate(p.date)}, prepare ${fmtDate(p.tomorrow)}`} />
      <Section title="Done today" count={p.done_today.length}>
        <Rows nodes={p.done_today} empty="Nothing marked done today." />
      </Section>
      <Section title="Open focus items" count={p.focus_open.length}>
        <Rows nodes={p.focus_open} empty="All focus items done." />
      </Section>
      <Section title="Due or overdue: do, move or drop" count={p.to_move.length}>
        <Rows
          nodes={p.to_move}
          empty="Nothing overdue."
          extra={(n) => (
            <Button
              size="sm"
              variant="outline"
              onClick={() => update.mutate({ id: n.id, due_date: p.tomorrow })}
            >
              Move to tomorrow
            </Button>
          )}
        />
      </Section>
      <Section title={`Tomorrow, ${fmtDate(p.tomorrow)}`}>
        <Rows
          nodes={[...p.tomorrow_calendar.scheduled, ...p.tomorrow_calendar.due]}
          empty="Nothing scheduled or due."
        />
        {p.tomorrow_calendar.rituals.map((r) => (
          <div key={`${r.type}-${r.at}`} className="mt-1 text-sm text-muted-fg">
            {r.type === 'daily_prep' ? 'Daily prep' : 'Weekly review'} at {fmtDateTime(r.at)}
          </div>
        ))}
      </Section>
      {p.buddy_due.length > 0 && (
        <Section title="Buddy check-ins due" count={p.buddy_due.length}>
          <ul className="space-y-1 text-sm">
            {p.buddy_due.map((b) => (
              <li key={b.id}>
                <span className="font-medium">{b.title}</span>
                {b.buddy_note && <div className="text-xs text-muted-fg">{b.buddy_note}</div>}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title={`Questions (template v${p.template.version})`}>
        <Questions questions={p.template.questions} answers={answers} setAnswers={setAnswers} />
      </Section>
      <Section title="Focus for tomorrow">
        {candidates.size === 0 && <Empty>No candidates. Mark tasks as next actions or set due dates.</Empty>}
        <ul className="space-y-1 text-sm">
          {[...candidates.entries()].map(([id, label]) => (
            <li key={id}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={focus.includes(id)}
                  onChange={(e) =>
                    setFocus(e.target.checked ? [...focus, id] : focus.filter((x) => x !== id))
                  }
                />
                {label}
              </label>
            </li>
          ))}
        </ul>
      </Section>
      <Card className="space-y-3 p-4">
        <div>
          <Label>Summary</Label>
          <Textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Save and confirm focus
        </Button>
      </Card>
      <PastReviews type="daily_prep" />
    </div>
  );
}

export function WeeklyReviewPage() {
  const date = todayLocal();
  const { data: prep, isLoading } = useQuery({
    queryKey: ['prep', 'weekly', date],
    queryFn: () => api.weeklyPrep(date),
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [insights, setInsights] = useState('');
  const navigate = useNavigate();
  const save = useMutation({
    mutationFn: () =>
      api.createReview({
        type: 'weekly_review',
        date,
        template_id: prep!.template.id,
        answers,
        summary_md: summary || undefined,
        insights_next_time: insights || undefined,
      }),
    onSuccess: () => {
      toast.success('Weekly review saved');
      navigate('/');
    },
  });
  if (isLoading || !prep) return <Spinner />;
  const p: WeeklyPrep = prep;
  return (
    <div className="space-y-4">
      <PageHeader title="Weekly review" subtitle={`${fmtDate(p.week_from)} – ${fmtDate(p.week_to)}`} />
      {p.last_insights?.insights_next_time && (
        <Card className="border-primary/40 p-4 text-sm">
          <span className="font-medium">From the last review ({fmtDate(p.last_insights.date)}):</span>{' '}
          {p.last_insights.insights_next_time}
        </Card>
      )}
      <Section title="Done this week" count={p.done_count}>
        {p.done_by_area.length === 0 && (
          <div className="text-sm text-muted-fg">Nothing done this week yet.</div>
        )}
        {p.done_by_area.map((g) => (
          <div key={`${g.area}|${g.goal}`} className="mb-2">
            <div className="text-xs text-muted-fg">
              {g.area}
              {g.goal && ` › ${g.goal}`}
            </div>
            <ul className="ml-3 list-disc text-sm">
              {g.items.map((i) => (
                <li key={i.id}>{i.title}</li>
              ))}
            </ul>
          </div>
        ))}
      </Section>
      {p.routine_adherence.length > 0 && (
        <Section title="Routines">
          <ul className="space-y-1 text-sm">
            {p.routine_adherence.map((r) => (
              <li key={r.id} className="flex justify-between">
                <span>{r.title}</span>
                <span className="text-muted-fg">
                  week {r.week.done}/{r.week.due} · 30 days {r.last_30_days.done}/{r.last_30_days.due} ·
                  streak {r.streak}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section
        title="Buddy focus"
        count={p.buddy_focus.filter((b) => b.status !== 'done' && b.status !== 'dropped').length}
      >
        <ul className="space-y-1 text-sm">
          {p.buddy_focus.map((b) => (
            <li key={b.id}>
              {b.title} {b.status !== 'active' && <Badge>{b.status}</Badge>}
            </li>
          ))}
        </ul>
        {p.proposals.length > 0 && (
          <div className="mt-2 space-y-1 rounded-xl bg-accent p-2 text-sm">
            {p.proposals.map((x) => (
              <div key={x}>💡 {x}</div>
            ))}
          </div>
        )}
      </Section>
      <HealthPanel compact />
      <Section title={`Questions (template v${p.template.version})`}>
        <Questions questions={p.template.questions} answers={answers} setAnswers={setAnswers} />
      </Section>
      <Card className="space-y-3 p-4">
        <div>
          <Label>Summary</Label>
          <Textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <div>
          <Label>Insights for next time</Label>
          <Textarea rows={2} value={insights} onChange={(e) => setInsights(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            Save review
          </Button>
          <Link to="/" className="self-center text-sm text-muted-fg hover:underline">
            Cancel
          </Link>
        </div>
      </Card>
      <PastReviews type="weekly_review" />
    </div>
  );
}
